import { createSelector } from '@reduxjs/toolkit';
import type { PusherEventPayloadMap } from '@timelyapp/integration-cloud-api-spec';
import { getTicApiSlice } from 'redux/modules/tic/api-slice/tic.api.selector';
import { EMPTY_ARRAY } from '~constants/defaults';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { IState } from '~redux/state';
import type { PendingSubscriptionChange } from './tic.integrations.slice';

// Base selectors
export const getIntegrationConfiguration = (state: IState) => state.ticIntegrations.configuration;
export const getSubscriptionQueryArgs = (state: IState) =>
  state.ticIntegrations.subscriptions.queryArgs;
export const getPendingChanges = (state: IState) =>
  state.ticIntegrations.subscriptions.pendingChanges;
export const getSubscriptionsState = (state: IState) => state.ticIntegrations.subscriptions;
export const getIntegrationJobs = (state: IState) => state.ticIntegrations.integrationJobs;

// RTKQuery-based selector
const getSubscribableResourcesRTKQueryBase = createSelector(
  [getTicApiSlice, getSubscriptionQueryArgs],
  (ticApiState, queryArgs) => {
    if (!queryArgs) {
      return { data: undefined, isLoading: false, error: undefined };
    }

    return ticApi.endpoints.getSubscribableResources.select(queryArgs)({
      [ticApi.reducerPath]: ticApiState,
    });
  }
);

// Apply pending changes to subscription data - trusting API structure
export const getEnrichedSubscribableResources = createSelector(
  [getSubscribableResourcesRTKQueryBase, getPendingChanges],
  (queryResult, pendingChanges) => {
    // Trust API contract - use stable empty array when no data
    const subscriptions = queryResult.data?.subscriptions || EMPTY_ARRAY;

    return subscriptions.map(resource => {
      const change = pendingChanges[resource.subscription_id];

      return {
        ...resource,
        enabled: change?.enabled ?? resource.enabled,
        sub_subscriptions:
          resource.sub_subscriptions?.map(sub => ({
            ...sub,
            enabled: pendingChanges[sub.subscription_id]?.enabled ?? sub.enabled,
          })) || null,
      };
    });
  }
);

/** Subscription counts using API data directly */
export const getSubscriptionCounts = createSelector(
  [getEnrichedSubscribableResources],
  resources => ({
    total: resources.length,
    enabled: resources.filter(r => r.enabled).length,
  })
);

/** Pending changes state and save capability */
export const getPendingChangesState = createSelector(
  [getPendingChanges, getSubscriptionQueryArgs, getIntegrationConfiguration],
  (
    pendingChanges,
    queryArgs,
    config
  ): {
    count: number;
    hasChanges: boolean;
    canSave: boolean;
    forSave: PendingSubscriptionChange[];
  } => {
    const changesArray = Object.values(pendingChanges);
    const hasChanges = changesArray.length > 0;
    const canSave = Boolean(
      queryArgs && config.toolId && config.toolId === queryArgs.provider && hasChanges
    );

    return {
      count: changesArray.length,
      hasChanges,
      canSave,
      forSave: changesArray,
    };
  }
);

// Modal state
export const getShowingIntegrationModal = (state: IState) =>
  state.ticIntegrations.configuration.modal;

export const getIsAnyIntegrationModalOpen = (state: IState) =>
  Boolean(state.ticIntegrations.configuration.modal);

// Integration Jobs selectors
/**
 * Get current running integration job based on configuration context.
 *
 * FUTURE USE: This selector will be integrated with TicNotifications to show
 * real-time job status in the calendar notification dropdown.
 *
 * Potential integration patterns:
 * - Show most recent job status as notification
 * - Display job progress in notification dropdown
 * - Toast notifications for job completion/failure
 *
 * @see apps/timely-frontend/src/routes/supercalendar/components/calendar/tic/TicNotifications.tsx
 */
export const getCurrentIntegrationJob = createSelector(
  [getIntegrationJobs, getIntegrationConfiguration],
  (integrationJobs, config) => {
    // Find job matching current configuration context
    return Object.values(integrationJobs).find(
      job => job.tool_id === config.toolId && job.status !== 'completed' && job.status !== 'failed'
    );
  }
);

/** Get integration job by specific job ID */
export const getIntegrationJobStatus = (jobId: string) =>
  createSelector(
    [getIntegrationJobs],
    (integrationJobs): PusherEventPayloadMap['integration_job_event'] | undefined =>
      integrationJobs[jobId]
  );

/**
 * Note: We intentionally do NOT provide a parameterized selector for tool+connection+job
 * combinations because:
 *
 * 1. It would create memoization issues (each call creates a new selector instance)
 * 2. Job ID-based tracking provides perfect job isolation
 * 3. Our current architecture uses unique job IDs for each job execution
 *
 * If you need to find jobs by these parameters, either:
 * - Use the job ID returned from ticApi.endpoints.providerOperationId.initiate()
 * - Create a proper selector factory with useMemo in your component
 */

/** Get all integration jobs by tool (for debugging/monitoring) */
export const getIntegrationJobsByTool = createSelector(
  [getIntegrationJobs, getIntegrationConfiguration],
  (integrationJobs, config) =>
    Object.values(integrationJobs).filter(job => job.tool_id === config.toolId)
);

/** Get active integration jobs count */
export const getActiveIntegrationJobsCount = createSelector(
  [getIntegrationJobs],
  integrationJobs => Object.values(integrationJobs).filter(job => job.status === 'started').length
);

/** Check if any integration job is currently running */
export const getHasRunningIntegrationJobs = createSelector(
  [getActiveIntegrationJobsCount],
  count => count > 0
);
